import { useMemo, useState } from 'react';
import { useTransactionStore } from '../state/useTransactionStore';
import {
  PieChart,
  Pie,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

// Format currency with 2 decimal places and thousands separator
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// Format percentage with 2 decimal places
const formatPercentage = (value: number): string => {
  return `${(value * 100).toFixed(2)}%`;
};

// Custom tooltip formatter for charts
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-gray-800 p-2 border border-gray-200 dark:border-gray-700 shadow rounded">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="text-sm text-gray-600 dark:text-gray-300" style={{ color: entry.color }}>
            {entry.name}: {formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

type TimeRange = 'month' | 'year';

export default function Dashboard() {
  const { transactions } = useTransactionStore();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [timeRange, setTimeRange] = useState<TimeRange>('month');

  const summary = useMemo(() => {
    const filteredTransactions = transactions.filter(t => {
      const date = new Date(t.date);
      if (timeRange === 'month') {
        return date.getMonth() === selectedDate.getMonth() && 
               date.getFullYear() === selectedDate.getFullYear();
      } else {
        return date.getFullYear() === selectedDate.getFullYear();
      }
    });

    const totalIncome = filteredTransactions
      .filter(t => t.type === 'credit')
      .reduce((sum, t) => sum + t.amount, 0);

    const totalExpenses = filteredTransactions
      .filter(t => t.type === 'debit')
      .reduce((sum, t) => sum + t.amount, 0);

    const savings = totalIncome - totalExpenses;

    return {
      income: totalIncome,
      expenses: totalExpenses,
      savings,
    };
  }, [transactions, selectedDate, timeRange]);

  const categoryData = useMemo(() => {
    const filteredTransactions = transactions.filter(t => {
      const date = new Date(t.date);
      if (timeRange === 'month') {
        return date.getMonth() === selectedDate.getMonth() && 
               date.getFullYear() === selectedDate.getFullYear();
      } else {
        return date.getFullYear() === selectedDate.getFullYear();
      }
    });

    const categoryTotals = filteredTransactions
      .filter(t => t.type === 'debit')
      .reduce((acc, t) => {
        const category = t.category || 'Uncategorized';
        acc[category] = (acc[category] || 0) + Math.abs(t.amount);
        return acc;
      }, {} as Record<string, number>);

    const total = Object.values(categoryTotals).reduce((sum, val) => sum + val, 0);

    return Object.entries(categoryTotals)
      .map(([name, value]) => ({
        name,
        value,
        percentage: value / total,
      }))
      .sort((a, b) => b.value - a.value);
  }, [transactions, selectedDate, timeRange]);

  const monthlyData = useMemo(() => {
    const filteredTransactions = transactions.filter(t => {
      const date = new Date(t.date);
      if (timeRange === 'month') {
        return date.getFullYear() === selectedDate.getFullYear();
      } else {
        return date.getFullYear() === selectedDate.getFullYear();
      }
    });

    const monthlyTotals = filteredTransactions.reduce((acc, t) => {
      const date = new Date(t.date);
      const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!acc[monthYear]) {
        acc[monthYear] = { income: 0, expenses: 0 };
      }
      
      if (t.type === 'credit') {
        acc[monthYear].income += t.amount;
      } else {
        acc[monthYear].expenses += t.amount;
      }
      
      return acc;
    }, {} as Record<string, { income: number; expenses: number }>);

    return Object.entries(monthlyTotals)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, data]) => ({
        month,
        income: Math.abs(data.income),
        expenses: Math.abs(data.expenses),
      }));
  }, [transactions, selectedDate, timeRange]);

  const handleDateChange = (months: number) => {
    const newDate = new Date(selectedDate);
    if (timeRange === 'month') {
      newDate.setMonth(newDate.getMonth() + months);
    } else {
      newDate.setFullYear(newDate.getFullYear() + months);
    }
    setSelectedDate(newDate);
  };

  const formatDateDisplay = () => {
    if (timeRange === 'month') {
      return selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    return selectedDate.getFullYear().toString();
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Dashboard</h2>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setTimeRange('month')}
              className={`px-3 py-1 rounded ${
                timeRange === 'month' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              Month
            </button>
            <button
              onClick={() => setTimeRange('year')}
              className={`px-3 py-1 rounded ${
                timeRange === 'year' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              Year
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleDateChange(-1)}
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              ←
            </button>
            <span className="font-medium text-gray-900 dark:text-gray-100">{formatDateDisplay()}</span>
            <button
              onClick={() => handleDateChange(1)}
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              →
            </button>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100">Income</h3>
          <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
            {formatCurrency(summary.income)}
          </p>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-red-900 dark:text-red-100">Expenses</h3>
          <p className="text-2xl font-bold text-red-700 dark:text-red-300">
            {formatCurrency(summary.expenses)}
          </p>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-green-900 dark:text-green-100">Savings</h3>
          <p className="text-2xl font-bold text-green-700 dark:text-green-300">
            {formatCurrency(summary.savings)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Spending by Category</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categoryData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, percentage }) => `${name} (${formatPercentage(percentage)})`}
                >
                  {categoryData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
          {/* Category Spending Grid */}
          <div className="mt-4">
            <div className="grid grid-cols-1 gap-2">
              {categoryData.map((category, index) => (
                <div 
                  key={category.name}
                  className="flex items-center justify-between p-2 rounded bg-gray-50 dark:bg-gray-700"
                >
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="font-medium text-gray-900 dark:text-gray-100">{category.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(category.value)}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{formatPercentage(category.percentage)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Monthly Trends</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyData} margin={{ left: 20, right: 20, top: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="month" 
                  tickFormatter={(value) => {
                    const [year, month] = value.split('-');
                    return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'short' });
                  }}
                  stroke="#9CA3AF"
                />
                <YAxis 
                  tickFormatter={formatCurrency}
                  width={80}
                  tick={{ fontSize: 12 }}
                  stroke="#9CA3AF"
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="income"
                  stroke="#0088FE"
                  name="Income"
                />
                <Line
                  type="monotone"
                  dataKey="expenses"
                  stroke="#FF8042"
                  name="Expenses"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
} 